/**
 * Endpoints #68-#76 and #94 — New Post Watching (SPEC `New Post Watching`).
 *
 * Watching is a **standalone subsystem that shares the identity layer** with Profile Monitoring:
 * the same profiles and groups, its own scheduler, check log, raw store and Telegram bot. This file
 * is the whole contract the SPA sees — the `watch` settings blob (§4.1 registry), the row-level
 * `watching` block #4/#11 gained, and the read/write models of #68-#72.
 *
 * Two conventions carry the dumb-UI rule (§12.5) here: every cadence is a **free integer minute
 * count** (1-1440 — a setting, not a code constant, so an agent can retune it through #69 without a
 * deploy), and every duration, money and "posted 2h ago" string is server-built and rendered
 * verbatim.
 */
import { z } from 'zod';
import { PROFILE_KINDS } from '../enums.js';
import { GlobalFeedQuerySchema } from './globalFeed.js';
/* --------------------------------------------------------------- availability */
/** The seven availability rows, Monday-first (Luxon's ISO weekday order). */
export const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
/** `HH:mm`, 00:00-23:59. */
const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
/** `HH:mm` plus the end-of-day sentinel. */
const END_TIME_PATTERN = /^(?:(?:[01]\d|2[0-3]):[0-5]\d|24:00)$/;
/** A fresh 24-hour window — the default every weekday starts on. */
export function fullDayRange() {
    return { start: '00:00', end: '24:00' };
}
/** Default availability: 24/7. Watching is gated by the master toggle, not by the calendar. */
export function watchAvailabilityDefault() {
    return {
        mon: fullDayRange(),
        tue: fullDayRange(),
        wed: fullDayRange(),
        thu: fullDayRange(),
        fri: fullDayRange(),
        sat: fullDayRange(),
        sun: fullDayRange(),
    };
}
/* -------------------------------------------------------------- settings blob */
/** Cadence bounds — every cadence field in this file, settings and overrides alike. */
export const WATCH_CADENCE_MIN_MINUTES = 1;
export const WATCH_CADENCE_MAX_MINUTES = 1440;
export const WATCH_DEFAULT_CADENCE_MINUTES = 15;
export const WATCH_DEFAULT_HOT_CADENCE_MINUTES = 5;
export const WATCH_DEFAULT_COLD_CADENCE_MINUTES = 60;
export const WATCH_DEFAULT_COST_PER_CHECK_USD = 0.001;
export function watchSettingsDefault() {
    return {
        enabled: false,
        cadenceMinutes: WATCH_DEFAULT_CADENCE_MINUTES,
        smart: false,
        hotCadenceMinutes: WATCH_DEFAULT_HOT_CADENCE_MINUTES,
        coldCadenceMinutes: WATCH_DEFAULT_COLD_CADENCE_MINUTES,
        availability: watchAvailabilityDefault(),
        costPerCheckUsd: WATCH_DEFAULT_COST_PER_CHECK_USD,
    };
}
/** Frozen default for the §4.1 registry table; mutable copies come from the factory above. */
export const WATCH_SETTINGS_DEFAULT = watchSettingsDefault();
const StoredRangeSchema = z.object({
    start: z.string().regex(TIME_PATTERN),
    end: z.string().regex(END_TIME_PATTERN),
});
/** Per **day**, not per blob: one corrupt weekday falls back alone and the other six survive. */
const StoredDaySchema = StoredRangeSchema.nullable()
    .catch(() => fullDayRange())
    .default(() => fullDayRange());
const StoredAvailabilitySchema = z
    .object({
    mon: StoredDaySchema,
    tue: StoredDaySchema,
    wed: StoredDaySchema,
    thu: StoredDaySchema,
    fri: StoredDaySchema,
    sat: StoredDaySchema,
    sun: StoredDaySchema,
})
    .catch(() => watchAvailabilityDefault())
    .default(() => watchAvailabilityDefault());
const storedCadence = (fallback) => z
    .number()
    .int()
    .min(WATCH_CADENCE_MIN_MINUTES)
    .max(WATCH_CADENCE_MAX_MINUTES)
    .catch(fallback)
    .default(fallback);
/**
 * Lenient decode of the stored blob: every field falls back rather than throwing, so a hand-edited
 * database can never crash a boot (same contract as the other §4.1 validators). The cross-field
 * `hot <= cold` rule is **not** applied here — a stored inversion still reads, and #69 is where it
 * is refused.
 */
const StoredWatchSettingsSchema = z.object({
    enabled: z.boolean().catch(false).default(false),
    cadenceMinutes: storedCadence(WATCH_DEFAULT_CADENCE_MINUTES),
    smart: z.boolean().catch(false).default(false),
    hotCadenceMinutes: storedCadence(WATCH_DEFAULT_HOT_CADENCE_MINUTES),
    coldCadenceMinutes: storedCadence(WATCH_DEFAULT_COLD_CADENCE_MINUTES),
    availability: StoredAvailabilitySchema,
    costPerCheckUsd: z
        .number()
        .positive()
        .catch(WATCH_DEFAULT_COST_PER_CHECK_USD)
        .default(WATCH_DEFAULT_COST_PER_CHECK_USD),
});
export function decodeWatchSettings(value) {
    const parsed = StoredWatchSettingsSchema.safeParse(value);
    return parsed.success ? parsed.data : watchSettingsDefault();
}
/* ------------------------------------------------------------------ #69 write */
/** `z.number()` already rejects NaN and ±Infinity, so "positive" is the whole rule. */
const CostSchema = z.number().positive();
const CadenceSchema = z
    .number()
    .int()
    .min(WATCH_CADENCE_MIN_MINUTES)
    .max(WATCH_CADENCE_MAX_MINUTES);
const AvailabilityRangeSchema = z
    .strictObject({
    start: z.string().regex(TIME_PATTERN, 'expected HH:mm'),
    end: z.string().regex(END_TIME_PATTERN, 'expected HH:mm (24:00 allowed)'),
})
    .refine((range) => range.start !== range.end, 'start and end must differ');
/** `null` = that weekday is Off. All seven keys are required whenever `availability` is sent. */
const AvailabilitySchema = z.strictObject({
    mon: AvailabilityRangeSchema.nullable(),
    tue: AvailabilityRangeSchema.nullable(),
    wed: AvailabilityRangeSchema.nullable(),
    thu: AvailabilityRangeSchema.nullable(),
    fri: AvailabilityRangeSchema.nullable(),
    sat: AvailabilityRangeSchema.nullable(),
    sun: AvailabilityRangeSchema.nullable(),
});
/**
 * #69 `PUT /api/watch/settings` — a **partial** blob, one section of the tab at a time.
 *
 * `hotCadenceMinutes <= coldCadenceMinutes` is deliberately absent: a partial patch cannot see both
 * halves, so the route validates the rule on the value it merged (the complete #72 draft below does
 * carry the refine, because a draft is whole by construction).
 */
export const WatchSettingsUpdateSchema = z
    .strictObject({
    enabled: z.boolean().optional(),
    cadenceMinutes: CadenceSchema.optional(),
    smart: z.boolean().optional(),
    hotCadenceMinutes: CadenceSchema.optional(),
    coldCadenceMinutes: CadenceSchema.optional(),
    availability: AvailabilitySchema.optional(),
    costPerCheckUsd: CostSchema.optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'nothing to update');
/* --------------------------------------------------------------- #94 snooze */
/** The five spellings of "pause it for a bit"; `'tomorrow'` means the next local midnight. */
export const WATCH_SNOOZE_PRESETS = ['30m', '1h', '2h', '4h', 'tomorrow'];
/**
 * #94 `POST /api/watch/snooze`. `preset: null` resumes; any other value replaces a standing snooze.
 *
 * Deliberately **not** a `WatchSettings` field: snooze is temporary server state (`app_state`), so it
 * can never ride a settings draft, be reverted by a concurrent #69 save, or move the #72 estimate.
 */
export const WatchSnoozeBodySchema = z.strictObject({
    preset: z.enum(WATCH_SNOOZE_PRESETS).nullable(),
});
/* ----------------------------------------------------------------- #70 target */
/**
 * #70 `PUT /api/watch/targets/:t/:id`. `cadenceMinutes`/`smart` are legal on **groups only** (400
 * on a profile); `null` clears an override back to inherit.
 */
export const WatchTargetBodySchema = z
    .strictObject({
    enabled: z.boolean().optional(),
    cadenceMinutes: CadenceSchema.nullable().optional(),
    smart: z.boolean().nullable().optional(),
})
    .refine((body) => Object.keys(body).length > 0, 'nothing to update');
/* ------------------------------------------------------------------- #71 feed */
/**
 * #71 `GET /api/watch/feed` — **#51 with a third scope**.
 *
 * Since the watch→analytics ingest bridge every detection is replayed into `posts` before it
 * commits, so "Posts to Comment" is the global feed narrowed to watch-caught posts: the same filter
 * vocabulary, the same sort keys, the same opaque keyset cursor and the same `FeedCardDTO`. The one
 * addition over #51's own client is `q` — the `/saved` search treatment minus the saved notes and
 * tags, which this screen has no place for.
 */
export const WatchFeedQuerySchema = GlobalFeedQuerySchema;
/* --------------------------------------------------------------- #72 estimate */
/** A group's overrides as the draft has them — unsaved, so the estimate can preview them. */
const EstimateGroupOverrideSchema = z.strictObject({
    cadenceMinutes: CadenceSchema.nullable().optional(),
    smart: z.boolean().nullable().optional(),
});
/**
 * #72 `POST /api/watch/estimate` — the live cost card.
 *
 * The draft is **complete** (the tab holds every field), so unlike #69 this schema carries the
 * `hot <= cold` refine. `enabled` rides along even though the SPEC's param list omits it: "master-off
 * drafts return $0" is a server decision, and the client must not do the arithmetic (§12.5).
 */
export const WatchEstimateBodySchema = z
    .strictObject({
    enabled: z.boolean(),
    cadenceMinutes: CadenceSchema,
    smart: z.boolean(),
    hotCadenceMinutes: CadenceSchema,
    coldCadenceMinutes: CadenceSchema,
    availability: AvailabilitySchema,
    costPerCheckUsd: CostSchema,
    /** Keyed by group id (JSON object keys are strings); absent = use the saved overrides. */
    groupOverrides: z
        .record(z.string().regex(/^[1-9][0-9]*$/, 'expected a group id'), EstimateGroupOverrideSchema)
        .optional(),
})
    .refine((body) => body.hotCadenceMinutes <= body.coldCadenceMinutes, 'hot cadence must not be slower than the quiet-hours cadence');
const NewPostPayloadSchema = z.object({
    watchDetectionId: z.number().int(),
    authorName: z.string().catch('').default(''),
    authorSlug: z.string().catch('').default(''),
    // v3 field: a v1/v2 row has no `authorKind`, and a person is what every one of them was.
    authorKind: z.enum(PROFILE_KINDS).catch('person').default('person'),
    url: z.string().nullable().catch(null).default(null),
    postType: z.string().catch('other').default('other'),
    publishedAt: z.number().int(),
    detectedAt: z.number().int(),
    preview: z.string().nullable().catch(null).default(null),
    postedAgoDisplay: z.string().catch('').default(''),
    // v4 field: `default` absorbs its absence on a v1–v3 row, `catch` absorbs a garbage value.
    potentialEngagement: z.number().int().min(0).nullable().catch(null).default(null),
    // v5 field: absent on every v1-v4 row, which decodes `null` and keeps its post-URL fallback.
    authorProfileUrl: z.string().nullable().catch(null).default(null),
});
/**
 * Lenient read-back: display halves fall back to a blank rather than throwing, but a payload with
 * no detection id or no timestamps is not a `new_post` payload at all — `null`, and the caller
 * skips the row (the `parseViralPayload` contract, one level stricter because there is no live
 * post to fall back to).
 *
 * Deliberately **not** `.strict()`: a v1 row still carries `notifiedInDisplay`, which payload v2
 * dropped. The extra key is ignored rather than rejected, so pre-feature rows keep rendering.
 */
export function decodeNewPostPayload(value) {
    const parsed = NewPostPayloadSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=watch.js.map